import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Row, Col } from 'react-bootstrap';

const Dropdown = ({ users, value, onChange }) => {
    return (
        <Form.Control as="select" name="inventor" value={value || ''} onChange={onChange}>
            <option value="" disabled hidden>Select inventor</option>
            {users.map(user => (
                <option key={user.userId} value={user.userId}>
                    {user.username} ({user.userId})
                </option>
            ))}
        </Form.Control>
    );
};

const DepartmentDropdown = ({ departments, value, onChange }) => {
    return (
        <Form.Control 
            as="select" 
            name="department" 
            value={value || ''} 
            onChange={onChange} 
            required
        >
            <option value="" disabled hidden>Select department</option>
            {departments.map(dept => (
                <option key={dept.deptId} value={dept.deptId}>
                    {dept.deptName} ({dept.deptId})
                </option>
            ))}
        </Form.Control>
    );
};

const PatentForm = () => {
    const [departments, setDepartments] = useState([]);
    const [patentId, setPatentId] = useState(null);
    const [inventors, setInventors] = useState([]);
    const [otherInventors, setOtherInventors] = useState([]);
    const [inventorOptions, setInventorOptions] = useState([]);
    const [formData, setFormData] = useState({
        departmentId: '',
        title: '',
        applicationNumber: '',
        year: '',
        filingDate: '',
        status: '',
        publishedDate: '',
        ferSubmittedDate: '',
        hearingDate: '',
        grantedDate: '',
        patentNumber: '',
        renewalDate: '',
        technologyReadinessLevel: '',
        inventor: '',
        otherInventor: '',
        commercialized: 'no',
        commercializationDescription: '',
        commercializationPercentage: '',
        fundsGenerated: '',
        description: '',
        commercializationDate: '',
        startDate: '',
        filePath: '',
    });

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/departments`);
                setDepartments(response.data);
            } catch (error) {
                console.error('Error fetching departments:', error);
            }
        };

        fetchDepartments();
    }, []);

    const [showCommercializationDetails, setShowCommercializationDetails] = useState(false);

    useEffect(() => {
        const fetchInventors = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users`);
                setInventorOptions(response.data);
            } catch (error) {
                console.error('Error fetching inventors:', error);
            }
        };

        fetchInventors();
    }, []);

    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        if (name === 'commercialized') {
            setShowCommercializationDetails(value === 'yes');

            if (value === 'no') {
                setFormData(prev => ({
                    ...prev,
                    commercializationDescription: '',
                    commercializationPercentage: '',
                    fundsGenerated: '',
                    description: '',
                    commercializationDate: '',
                    startDate: '',
                    filePath: '',
                }));
            }
        }
    };

    const addInventor = () => {
        if (formData.inventor && !inventors.includes(formData.inventor)) {
            setInventors([...inventors, formData.inventor]);
            setFormData({ ...formData, inventor: '' });
        }
    };

    const addOtherInventor = () => {
        if (formData.otherInventor && !otherInventors.includes(formData.otherInventor)) {
            setOtherInventors([...otherInventors, formData.otherInventor]);
            setFormData({ ...formData, otherInventor: '' });
        }
    };

    const removeInventor = (index) => {
        const newInventors = inventors.filter((_, i) => i !== index);
        setInventors(newInventors);
    };

    const removeOtherInventor = (index) => {
        const newOtherInventors = otherInventors.filter((_, i) => i !== index);
        setOtherInventors(newOtherInventors);
    };

    const handlePatentSubmit = async (event) => {
        event.preventDefault();
        console.log('Form Data before submit:', formData);

        try {
            const submitData = {
                ...formData,
                inventors: inventors.join(', '),
                otherInventors: otherInventors.join(', '),
                department: formData.departmentId,
            };
            console.log('Submitting data:', submitData); 
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/patents`, submitData);
            clearForm();
            console.log(response.status);
            console.log(response);
           
            
            if (response.status === 200 || response.status === 201) {
                const newPatentId = response.data.id;  // ✅ Extract the correct ID
                setPatentId(newPatentId);  // ✅ Save it in state
                console.log('New Patent ID:', newPatentId);
                alert('Patent form submitted! Patent ID: ' + newPatentId);

                if (formData.commercialized === 'yes' && newPatentId) {
                    await handleCommercializationSubmit(newPatentId); // 
                }
                
            } 
        } catch (error) {
            console.error('Error:', error); // Log the entire error object for more context
            if (error.response) {
                console.error('Error response data:', error.response.data);
                alert(`Error: ${error.response.data.message || 'Server error'}`);
            } else if (error.request) {
                console.error('Error request:', error.request);
                alert('Error: No response from server.');
            } else {
                alert('An unexpected error occurred: ' + error.message);
            }
        }
    };

    const handleCommercializationSubmit = async (id) => {
        try {
            const commercializationData = new FormData();
            commercializationData.append('commercializationDescription', formData.commercializationDescription.trim());
            commercializationData.append('commercializationPercentage', parseFloat(formData.commercializationPercentage.trim()) || 0);
            commercializationData.append('fundsGenerated', parseFloat(formData.fundsGenerated.trim()) || 0);
            commercializationData.append('commercializationDate', formData.commercializationDate);
            commercializationData.append('startDate', formData.startDate);
            commercializationData.append('file', selectedFile); // Ensure this is a file object!
   
            console.log('Commercialization Data:', commercializationData);
            console.log('Patent ID:', id);
   
            if (!commercializationData.get('commercializationDescription') || !id) {
                throw new Error('Commercialization Description and Patent ID are required.');
            }
   
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/commercializations/${id}`,
                commercializationData,  // Send as FormData
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',  // Axios will handle this automatically, but keep it if needed.
                    },
                }
            );
   
            console.log('Response:', response.data);
            alert('Commercialization data added successfully!');
        } catch (error) {
            console.error('Error adding commercialization data:', error);
            if (error.response) {
                console.error('Server responded with:', error.response.data);
                alert(`Error: ${error.response.data.message || 'An error occurred'}`);
            } else {
                alert('Error adding commercialization data. Please try again.');
            }
        }
    };
   
        const clearForm = () => {
        setFormData({
            departmentId: '',
            title: '',
            applicationNumber: '',
            year: '',
            filingDate: '',
            status: '',
            publishedDate: '',
            ferSubmittedDate: '',
            hearingDate: '',
            grantedDate: '',
            patentNumber: '',
            renewalDate: '',
            technologyReadinessLevel: '',
            inventor: '',
            otherInventor: '',
            commercialized: 'no',
            commercializationDescription: '',
            commercializationPercentage: '',
            fundsGenerated: '',
            description: '',
            commercializationDate: '',
            startDate: '',
            filePath: '',
        });
        setInventors([]);
        setOtherInventors([]);
        setShowCommercializationDetails(false);
    };

    const statusOptions = [
        'Filed', 'Published', 'FER Submitted', 'Hearing Completed', 'Granted',
        'Patents granted and active', 'Patents granted but not renewed', 'Patents Legally Ceased',
        'Application FER reply filed', 'Application in FER hearing', 'Applications requested for examination',
        'Applications awaiting examination', 'Applications published', 'Applications awaiting publication',
        'Applications awaiting complete specification', 'Applications refused', 'Applications Abandoned'
    ];

    const readinessLevels = [
        '1. Basic Principles Observed',
        '2. Technology Concept Formulated',
        '3. Experimental Proof of Concept',
        '4. Technology Validated in Lab',
        '5. Technology Validated in Relevant Environment',
        '6. System Prototype Demonstrated in Relevant Environment',
        '7. System Prototype Demonstrated in Operational Environment',
        '8. System Completed and Qualified',
        '9. Actual System Proven in Operational Environment'
    ];

    return (
        <div className="patent-form-container d-flex justify-content-center align-items-center min-vh-100 bg-light">
            <Form className="patent-form bg-white p-4 rounded shadow" onSubmit={handlePatentSubmit}>
                <h2 className="text-center mb-4">Add Patent</h2>

                <Form.Group controlId="formDepartment">
                    <Form.Label>Department</Form.Label>
                    <DepartmentDropdown
                        departments={departments}
                        value={formData.departmentId}
                        onChange={(e) => {
                            const selectedDept = departments.find(dept => dept.deptId === e.target.value);
                            if (selectedDept) {
                                setFormData(prev => ({
                                    ...prev,
                                    departmentId: selectedDept.deptId,
                                    departmentName: selectedDept.deptName, // Optional: store name if needed
                                }));
                            }
                        }}
                    />
                </Form.Group>

                <Form.Group controlId="formTitle">
                    <Form.Label>Title of Innovation</Form.Label>
                    <Form.Control
                        type="text" name="title" value={formData.title} onChange={handleChange} required />
                </Form.Group>

                <Row>
                    <Col>
                        <Form.Group controlId="formApplicationNumber">
                            <Form.Label>Application Number</Form.Label>
                            <Form.Control type="text" name="applicationNumber" value={formData.applicationNumber} onChange={handleChange} />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group controlId="formYear">
                            <Form.Label>Academic Year</Form.Label>
                            <Form.Control type="text" name="year" value={formData.year} onChange={handleChange} required />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group controlId="formFilingDate">
                            <Form.Label>Date of Filing</Form.Label>
                            <Form.Control type="date" name="filingDate" value={formData.filingDate} onChange={handleChange} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formInventors">
                            <Form.Label>Inventors</Form.Label>
                            <div className="d-flex">
                                <Dropdown users={inventorOptions} value={formData.inventor} onChange={handleChange} />
                                <Button variant="primary" onClick={addInventor}>Add</Button>
                            </div>
                            <div className="mt-2">
                                {inventors.map((inv, index) => (
                                    <span key={index} className="badge bg-secondary me-1">
                                        {inv} <Button variant="danger" size="sm" onClick={() => removeInventor(index)}>x</Button>
                                    </span>
                                ))}
                            </div>
                        </Form.Group>
                    </Col>

                    <Col>
                        <Form.Group controlId="formOtherInventors">
                            <Form.Label>Other Inventors</Form.Label>
                            <div className="d-flex">
                                <Form.Control type="text" name="otherInventor" value={formData.otherInventor || ''} onChange={handleChange} />
                                <Button variant="primary" onClick={addOtherInventor}>Add</Button>
                            </div>
                            <div className="mt-2">
                                {otherInventors.map((oinv, index) => (
                                    <span key={index} className="badge bg-secondary me-1">
                                        {oinv} <Button variant="danger" size="sm" onClick={() => removeOtherInventor(index)}>x</Button>
                                    </span>
                                ))}
                            </div>
                        </Form.Group>
                    </Col>

                    <Col>
                        <Form.Group controlId="formStatus">
                            <Form.Label>Status</Form.Label>
                            <Form.Control as="select" name="status" value={formData.status} onChange={handleChange} required>
                                <option value="" disabled hidden>Select status</option>
                                {statusOptions.map((status, index) => (
                                    <option key={index} value={status}>{status}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                    <Form.Group controlId="formPublishedDate">
    <Form.Label>Published Date</Form.Label>
    <Form.Control
        type="datetime-local"
        name="publishedDate"
        value={formData.publishedDate}
        onChange={handleChange}
    />
</Form.Group>
                    </Col>
                    <Col>
                    <Form.Group controlId="formFerSubmittedDate">
    <Form.Label>FER Submitted Date</Form.Label>
    <Form.Control
        type="datetime-local"
        name="ferSubmittedDate"
        value={formData.ferSubmittedDate}
        onChange={handleChange}
    />
</Form.Group>

                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                    <Form.Group controlId="formHearingDate">
    <Form.Label>Hearing Date</Form.Label>
    <Form.Control
        type="datetime-local"
        name="hearingDate"
        value={formData.hearingDate}
        onChange={handleChange}
    />
</Form.Group>
                    </Col>
                    <Col>
                    <Form.Group controlId="formGrantedDate">
    <Form.Label>Granted Date</Form.Label>
    <Form.Control
        type="datetime-local"
        name="grantedDate"
        value={formData.grantedDate}
        onChange={handleChange}
    />
</Form.Group>
                    </Col>
                </Row>

                <Form.Group controlId="formPatentNumber">
                    <Form.Label>Patent Number</Form.Label>
                    <Form.Control type="text" name="patentNumber" value={formData.patentNumber} onChange={handleChange} />
                </Form.Group>
                <Form.Group controlId="formRenewalDate">
                    <Form.Label>Renewal Date</Form.Label>
                    <Form.Control 
                        type="datetime-local" 
                        name="renewalDate" 
                        value={formData.renewalDate} 
                        onChange={handleChange} 
                         
                    />
                </Form.Group>
                <Form.Group controlId="formTechnologyReadinessLevel">
                    <Form.Label>Technology Readiness Level</Form.Label>
                    <Form.Control as="select" name="technologyReadinessLevel" value={formData.technologyReadinessLevel} onChange={handleChange}>
                        <option value="" disabled hidden>Select level</option>
                        {readinessLevels.map(level => (
                            <option key={level} value={level}>{level}</option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group controlId="formCommercialized">
                    <Form.Label>Commercialized</Form.Label>
                    <Form.Control as="select" name="commercialized" value={formData.commercialized} onChange={handleChange}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                    </Form.Control>
                </Form.Group>

                {showCommercializationDetails && (
                    <div className="commercialization-details">
                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="formCommercializationDescription">
                                    <Form.Label>Commercialization Description / Partner Details</Form.Label>
                                    <Form.Control type="text" name="commercializationDescription" value={formData.commercializationDescription} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="formCommercializationPercentage">
                                    <Form.Label>Commercialization Percentage</Form.Label>
                                    <Form.Control type="text" name="commercializationPercentage" value={formData.commercializationPercentage} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group controlId="formFundsGenerated">
                                    <Form.Label>Funds Generated</Form.Label>
                                    <Form.Control type="text" name="fundsGenerated" value={formData.fundsGenerated} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="formCommercializationDate">
                                    <Form.Label>Commercialization Date</Form.Label>
                                    <Form.Control type="date" name="commercializationDate" value={formData.commercializationDate} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group controlId="formStartDate">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group controlId="formFilePath">
                            <Form.Label>File Path</Form.Label>
                            <Form.Control type="file" name="filePath" onChange={handleFileChange} required />
                        </Form.Group>
                    </div>
                )}

                <div className="d-flex justify-content-center mt-4">
                    <Button type="submit" variant="primary" className="me-3" style={{ width: '48%' }}>Submit</Button>
                    <Button variant="danger" onClick={clearForm} style={{ width: '48%' }}>Clear</Button>
                </div>
            </Form>
        </div>
    );
};

export default PatentForm;